/**
 * @fileoverview InventoryRecordService - Service for managing inventory records
 */

import { pb } from '../helpers/pocketbase_helper';
import { createModuleLogger } from '../utils/logger';

const logger = createModuleLogger('InventoryRecordService');

/**
 * Service for managing inventory records (producto_inventario collection)
 * Handles creation and management of inventory records for materials
 */
export const InventoryRecordService = {
  /**
   * Create a new inventory record for a material
   * @param {string} materialId - ID of the material (from corte_conversion)
   * @param {string} materialName - Name of the material
   * @param {number} quantity - Initial quantity (default: 0)
   * @returns {Promise<Object>} Created inventory record
   */
  async createInventoryRecord(materialId, materialName, quantity = 0) {
    try {
      logger.info('Creating inventory record with total=0', {
        materialId,
        materialName,
        quantity: 'Will be handled by solicitud_inventario'
      });

      if (!materialId || materialId.trim() === '') {
        throw new Error('Material ID is required');
      }

      if (!materialName || materialName.trim() === '') {
        throw new Error('Material name is required');
      }

      const usuarioId = pb.authStore.model?.id;
      logger.debug('Checking user authentication', {
        usuarioId,
        hasAuthStore: !!pb.authStore,
        hasModel: !!pb.authStore.model
      });
      
      if (!usuarioId) {
        throw new Error('User must be authenticated to create inventory records');
      }

      // materialId is already the actual material ID, no need to fetch from corte_conversion
      const actualMaterialId = materialId;
      
      const recordData = {
        material: actualMaterialId, // Use the actual material ID from papel relation
        // Don't include tinta field when it's null to avoid unique constraint violation
        total: 0, // Always create with 0, the actual quantity goes in solicitud_inventario
        reservado: 0,
        solicitado: 0,
        usuario: usuarioId,
        activo: true
      };

      logger.debug('Creating inventory record with data', {
        recordData,
        actualMaterialId,
        quantity
      });

      const result = await pb.collection('producto_inventario').create(recordData);

      logger.info('Inventory record created successfully', {
        recordId: result.id,
        materialId,
        materialName,
        quantity
      });

      return result;

    } catch (error) {
      logger.error('Error creating inventory record', error, {
        materialId,
        materialName,
        quantity,
        errorMessage: error.message,
        errorData: error.data,
        errorStatus: error.status
      });
      throw error;
    }
  },

  /**
   * Ensure inventory record exists for a material
   * Validates that material has producto_inventario assigned and returns it
   * @param {string} materialId - ID of the material
   * @param {string} materialName - Name of the material (optional)
   * @param {number} quantity - Initial quantity for new records (default: 0) - NOT USED, kept for compatibility
   * @returns {Promise<Object>} producto_inventario record assigned to the material
   */
  async ensureInventoryExists(materialId, materialName = null, quantity = 0) {
    try {
      logger.info('Ensuring inventory exists', { materialId, materialName });

      if (!materialId || materialId.trim() === '') {
        throw new Error('Material ID is required');
      }

      // Validate that material has producto_inventario assigned
      let material;
      try {
        material = await pb.collection('material').getOne(materialId, {
          expand: 'producto_inventario'
        });
      } catch (err) {
        // If material doesn't exist, try as tinta
        try {
          const tinta = await pb.collection('tinta').getOne(materialId);
          // For tinta, we need to check if there's a producto_inventario with this tinta
          const productoInventario = await pb.collection('producto_inventario').getList(1, 1, {
            filter: `tinta = "${materialId}" && activo = true`
          });
          if (productoInventario.items.length === 0) {
            throw new Error(`El material/tinta "${tinta.nombre || materialId}" no tiene producto_inventario asignado. Por favor, asigne un producto_inventario al material antes de crear la solicitud.`);
          }
          // Return the producto_inventario found for tinta
          logger.info('Producto inventario found for tinta', {
            productoInventarioId: productoInventario.items[0].id,
            materialId
          });
          return productoInventario.items[0];
        } catch (tintaErr) {
          throw new Error(`Material "${materialId}" no encontrado o no tiene producto_inventario asignado. Por favor, asigne un producto_inventario al material antes de crear la solicitud.`);
        }
      }

      // If material exists, validate producto_inventario
      if (material) {
        if (!material.producto_inventario || material.producto_inventario.trim() === '') {
          const materialName = material.nombre || materialId;
          throw new Error(`El material "${materialName}" no tiene producto_inventario asignado. Por favor, asigne un producto_inventario al material antes de crear la solicitud.`);
        }

        // Get the producto_inventario record
        const productoInventario = await pb.collection('producto_inventario').getOne(material.producto_inventario);

        logger.info('Producto inventario found for material', {
          productoInventarioId: productoInventario.id,
          materialId,
          materialName: material.nombre
        });

        return productoInventario;
      }

    } catch (error) {
      logger.error('Error ensuring inventory exists', error, { materialId });
      throw error;
    }
  },

  /**
   * Find existing inventory record for a material
   * @param {string} materialId - ID of the material (from corte_conversion)
   * @returns {Promise<Object|null>} Existing inventory record or null
   */
  async findInventoryRecord(materialId) {
    try {
      logger.debug('Searching for existing inventory record', { materialId });

      // materialId is already the actual material ID, no need to fetch from corte_conversion
      const actualMaterialId = materialId;

      // Check for existing inventory record with the actual material ID
      const result = await pb.collection('producto_inventario').getList(1, 1, {
        filter: `material = "${actualMaterialId}" && activo = true`
      });

      if (result.items.length > 0) {
        logger.debug('Found inventory record', {
          recordId: result.items[0].id,
          materialId,
          actualMaterialId
        });
        return result.items[0];
      }

      logger.debug('No existing inventory record found', { materialId, actualMaterialId });
      return null;

    } catch (error) {
      logger.error('Error finding inventory record', error, { materialId });
      throw error;
    }
  },


  /**
   * Update inventory record quantities
   * @param {string} recordId - ID of the inventory record
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated inventory record
   */
  async updateInventoryRecord(recordId, updates) {
    try {
      logger.info('Updating inventory record', { recordId, updates });

      if (!recordId || recordId.trim() === '') {
        throw new Error('Record ID is required');
      }

      const result = await pb.collection('producto_inventario').update(recordId, updates);

      logger.info('Inventory record updated successfully', {
        recordId,
        updates
      });

      return result;

    } catch (error) {
      logger.error('Error updating inventory record', error, { recordId, updates });
      throw error;
    }
  },

  /**
   * Get inventory record by ID
   * @param {string} recordId - ID of the inventory record
   * @returns {Promise<Object>} Inventory record
   */
  async getInventoryRecord(recordId) {
    try {
      logger.debug('Getting inventory record', { recordId });

      if (!recordId || recordId.trim() === '') {
        throw new Error('Record ID is required');
      }

      const result = await pb.collection('producto_inventario').getOne(recordId);

      logger.debug('Inventory record retrieved', {
        recordId,
        total: result.total,
        reservado: result.reservado,
        solicitado: result.solicitado
      });

      return result;

    } catch (error) {
      logger.error('Error getting inventory record', error, { recordId });
      throw error;
    }
  }
};
